import React, { useCallback, useState, useRef, useEffect } from "react";
import { Box, Paper, IconButton } from "@mui/material";
import LinkIcon from "../../../../../../../../../../../../../../Assets/Icons/link.svg";
import Trash from "../../../../../../../../../../../../../../Assets/Icons/trash-purple.svg";
import Document from "../../../../../../../../../../../../../../Assets/Icons/document-upload.svg";
import FaceDetect from "./FaceDetect";
import "./ImageUploader.scss";

const paperStyling = {
  border: "none",
  boxSizing: "border-box",
  position: "relative",
  background: "#fff1f2",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 10,
};

const labelStyling = {
  position: "absolute",
  width: "100%",
  height: "100%",
  placeContent: "center",
  display: "flex",
  top: "0",
  left: "0",
  textAlign: "center",
  cursor: "pointer",
};

export default function ImageUpload({
  inputId,
  label,
  title,
  onUploadFile,
  uploadedFile,
  uploadedUrl,
  onDeleteFile,
  boundingBox,
  boundingBoxSelectedIndexes,
  setBoundingBoxSelectedIndexes,
  swapFaceImagePath,
  num,
  withPointer,
  grid,
  width = 225,
  height = 255,
  persistSize = false,
}) {
  const [dragOver, setDragOver] = useState(false);
  const [imageSrc, setImageSrc] = useState(uploadedUrl);
  const [inputKey, setInputKey] = useState(Date.now());
  const inputRef = useRef();

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      setDragOver(false);
      if (event.dataTransfer.files && event.dataTransfer.files[0]) {
        const file = event.dataTransfer.files[0];
        const reader = new FileReader();
        reader.onload = (e) => {
          setImageSrc(e.target.result);
          onUploadFile?.(file); // Assuming this is for any additional handling
          setInputKey(Date.now()); // Reset input key to force re-render
        };
        reader.readAsDataURL(file);
      }
    },
    [onUploadFile]
  );

  const handleChange = useCallback(
    (event) => {
      if (event.target.files && event.target.files[0]) {
        const file = event.target.files[0];
        const reader = new FileReader();
        reader.onload = (e) => {
          setImageSrc(e.target.result);
          onUploadFile?.(file); // Assuming this is for any additional handling
          setInputKey(Date.now()); // Reset input key to force re-render
        };
        reader.readAsDataURL(file);
      }
    },
    [onUploadFile]
  );

  const handleDelete = () => {
    setImageSrc("");
    onDeleteFile?.();
  };

  const toggleUploadFile = () => {
    inputRef.current?.click();
  };

  const handleSelectFace = (component, index) => {
    setBoundingBoxSelectedIndexes?.((prevIndices) => ({
      ...prevIndices,
      [component]: index,
    }));
  };

  useEffect(() => {
    setImageSrc(uploadedUrl || "");
  }, [uploadedUrl]);

  return (
    <div className={`image-uploader ${grid ? "grid" : ""}`}>
      <input
        key={inputKey}
        ref={inputRef}
        accept="image/*"
        style={{ display: "none" }}
        id={`${inputId}-raised-button-file`}
        multiple={false}
        type="file"
        onChange={handleChange}
      />
      {title && <p className="title">{title}</p>}
      {withPointer && <div className="pointer">{num}</div>}
      {imageSrc && uploadedFile !== "" ? (
        <Paper
          variant="outlined"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{
            ...paperStyling,
            ...(persistSize && {
              width: width,
              height: height,
            }),
          }}
        >
          <div style={{ position: "relative" }}>
            {boundingBox?.length ? (
              <div
                className="image-input-face-selection"
                style={{
                  maxWidth: width,
                  maxHeight: height,
                }}
              >
                <FaceDetect
                  imageUrl={
                    swapFaceImagePath
                      ? window.localStorage.getItem("BaseURL") +
                        swapFaceImagePath
                      : imageSrc
                  }
                  boundingBoxes={boundingBox}
                  Name="second"
                  selectedIndex={boundingBoxSelectedIndexes?.second}
                  onSelect={handleSelectFace}
                  width={width - (persistSize ? 60 : 40)}
                  height={height - (persistSize ? 60 : 40)}
                />
              </div>
            ) : (
              <img
                src={imageSrc}
                alt="Uploaded"
                style={{
                  maxWidth: width - (persistSize ? 60 : 40),
                  maxHeight: height - (persistSize ? 60 : 40),
                  objectFit: "contain",
                  borderRadius: 10,
                }}
              />
            )}
            <section className="uploaded-image-actions">
              <img alt="" src={Trash} onClick={() => handleDelete()} />
              <img alt="" src={Document} onClick={toggleUploadFile} />
            </section>
          </div>
        </Paper>
      ) : (
        <Paper
          variant="outlined"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{
            ...paperStyling,
            width: width,
            height: height,
          }}
        >
          <label htmlFor={`${inputId}-raised-button-file`} style={labelStyling}>
            <Box display="flex" flexDirection="row" alignItems="center">
              <IconButton
                color="primary"
                aria-label="upload picture"
                component="span"
              >
                <img alt="" src={LinkIcon} />
              </IconButton>
              <p style={{ color: "#171717", fontSize: "16px" }}>{label}</p>
            </Box>
          </label>
        </Paper>
      )}
    </div>
  );
}
